import { PermissionGuard } from '@/components/PermissionGuard';
import { SiteEditDialog } from '@/components/SiteEditDialog';
import { Button } from '@/components/ui/button';
import { useSyncOrganization } from '@/hooks/useSyncOrganization';
import type { Organization } from '@octave/contracts';
import { Loader2, MapPin, Pencil, RefreshCw } from 'lucide-react';
import { useState } from 'react';

type OrganizationSite = Organization['sites'][number];

interface BillingCentersSectionProps {
  organization: Organization | undefined;
}

export function BillingCentersSection({
  organization,
}: BillingCentersSectionProps) {
  const { mutate: syncOrganization, isPending: isSyncing } =
    useSyncOrganization();
  const [editingSite, setEditingSite] = useState<OrganizationSite | null>(null);

  return (
    <section>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-slate-900">
          Centres de facturation
        </h2>
        <PermissionGuard permission="manage">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 border-slate-200 text-slate-600 hover:bg-slate-50"
            onClick={() =>
              organization?.id &&
              syncOrganization({ organizationId: organization.id })
            }
            disabled={isSyncing || !organization?.id}
          >
            {isSyncing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            Synchroniser
          </Button>
        </PermissionGuard>
      </div>

      {organization?.sites && organization.sites.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {organization.sites.map((site) => (
            <div
              key={site.id}
              className="bg-white rounded-xl p-5 hover:shadow-md transition-all border border-transparent hover:border-emerald-100 flex items-start gap-3"
            >
              <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-emerald-700">
                  {site.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <span className="font-medium">{site.name}</span>
                {site.address && (
                  <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                    <MapPin className="h-3 w-3 shrink-0" />
                    {`${site.address.street || ''} ${site.address.zipCode || ''} ${site.address.city || ''}`.trim()}
                  </p>
                )}
                {site.address?.complement && (
                  <p className="text-xs text-slate-400 mt-0.5 pl-4">
                    {site.address.complement}
                  </p>
                )}
              </div>
              <PermissionGuard permission="write">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 shrink-0 text-slate-400 hover:text-slate-700"
                  onClick={() => setEditingSite(site)}
                  aria-label={`Modifier ${site.name}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </PermissionGuard>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-500 py-4">
          Aucun centre de facturation configuré
        </p>
      )}

      {editingSite && organization && (
        <SiteEditDialog
          open
          onOpenChange={(open) => !open && setEditingSite(null)}
          site={editingSite}
          organizationId={organization.id}
        />
      )}
    </section>
  );
}
